/**
 * Builder do export do Controle PLD (#205) — CSV/XLSX (ExportDropdown) + PDF
 * (gerarPdfRelatorio). Função pura, testável sem render (estilo drePdf.ts).
 *
 * Monta, pro submercado + mês selecionados:
 *  - aba principal "PLD diário": realizado + projetado (flag do endpoint),
 *    com colunas P10/P90 só quando há projeção (mês corrente);
 *  - aba "Semanas Operativas"; aba "Resumo" (KPIs + fechamento + premissa);
 *  - PdfInput equivalente (Resumo + PLD diário + Semanas).
 *
 * Reusa FormattedCell (#74) pros numéricos (Excel agrega) e o pdfExport (#186).
 */
import type {
  PldOverviewResponse,
  PldProjecaoResponse,
  PldSemanasResponse,
  PldSubmercado,
} from "../../../api/types";
import type { ExportColumn, ExtraSheet } from "../../ui/ExportDropdown";
import type { PdfInput } from "../../../utils/pdfExport";
import { dateCell, int, num } from "../../../utils/xlsxHelpers";
import {
  SUBMERCADO_LABEL,
  fmtDateBR,
  fmtPld,
  mesLabel,
  statusBadge,
} from "./pldShared";

export interface PldExportInput {
  mesRef: string;
  submercado: PldSubmercado;
  overview: PldOverviewResponse | null;
  projecao: PldProjecaoResponse | null;
  semanas: PldSemanasResponse | null;
}

type MainRow = { data: string; tipo: string; pld: number | null; p10: number | null; p90: number | null };
type SemanaRow = Record<string, unknown>;
type ResumoRow = Record<string, unknown>;

export interface PldExportBuilt {
  filename: string;
  temProjecao: boolean;
  mainColumns: ExportColumn<MainRow>[];
  mainRows: MainRow[];
  extraSheets: ExtraSheet[];
  pdfInput: PdfInput;
}

function pdfNum(v: number | null | undefined): string {
  return v === null || v === undefined ? "—" : fmtPld(v);
}

/** Constrói o pacote de export, ou null quando não há dado pra exportar. */
export function buildPldExport(input: PldExportInput): PldExportBuilt | null {
  const { mesRef, submercado, overview, projecao, semanas } = input;

  // Linhas do "PLD diário": projeção (superset realizado+projetado) quando há;
  // senão, só o realizado do overview.
  let mainRows: MainRow[];
  if (projecao && Array.isArray(projecao.dias) && projecao.dias.length > 0) {
    mainRows = projecao.dias.map((d) => ({
      data: d.data,
      tipo: d.realizado ? "Realizado" : "Projetado",
      pld: d.pld_projetado,
      p10: d.realizado ? null : d.p10,
      p90: d.realizado ? null : d.p90,
    }));
  } else if (overview && Array.isArray(overview.dados_diarios) && overview.dados_diarios.length > 0) {
    mainRows = overview.dados_diarios.map((d) => ({
      data: d.data,
      tipo: "Realizado",
      pld: d.pld_media_dia,
      p10: null,
      p90: null,
    }));
  } else {
    return null; // nada a exportar
  }

  const temProjecao = mainRows.some((r) => r.tipo === "Projetado");
  const filename = `controle-pld_${submercado}_${mesRef}`;

  // ---- aba principal: colunas (P10/P90 só quando há projeção) ----
  const mainColumns: ExportColumn<MainRow>[] = [
    { key: "data", header: "Data", format: (r) => dateCell(r.data) },
    { key: "tipo", header: "Tipo", format: (r) => r.tipo },
    { key: "pld", header: "PLD (R$/MWh)", format: (r) => num(r.pld) },
  ];
  if (temProjecao) {
    mainColumns.push(
      { key: "p10", header: "P10 (R$/MWh)", format: (r) => (r.p10 != null ? num(r.p10) : null) },
      { key: "p90", header: "P90 (R$/MWh)", format: (r) => (r.p90 != null ? num(r.p90) : null) },
    );
  }

  // ---- aba Semanas Operativas ----
  // #229: a linha cross-month é a REV0 do mês seguinte — rotula com o mês de
  // origem ("REV0 (jul/26, próx.)") pra não virar uma "REV0" duplicada e confusa.
  const semanaRows: SemanaRow[] = (semanas?.semanas ?? []).map((s) => ({
    revisao:
      s.cross_month && s.mes_ref_origem
        ? `${s.revisao} (${mesLabel(s.mes_ref_origem)}, próx.)`
        : s.revisao,
    periodo: `${fmtDateBR(s.periodo_inicio)} – ${fmtDateBR(s.periodo_fim)}`,
    status: statusBadge(s.status).label,
    pld_medio: s.pld_medio,
    dias: s.dias,
  }));
  const semanaColumns: ExportColumn<SemanaRow>[] = [
    { key: "revisao", header: "Revisão", format: (r) => String(r.revisao) },
    { key: "periodo", header: "Período", format: (r) => String(r.periodo) },
    { key: "status", header: "Status", format: (r) => String(r.status) },
    { key: "pld_medio", header: "PLD médio (R$/MWh)", format: (r) => num(r.pld_medio as number | null) },
    { key: "dias", header: "Dias", format: (r) => int(r.dias as number) },
  ];

  // ---- aba Resumo (Campo/Valor) ----
  const prem = projecao?.premissa_decomp ?? null;
  interface ResumoEntry { campo: string; valor?: number | null; texto?: string }
  const entradas: ResumoEntry[] = [
    { campo: "Submercado", texto: `${SUBMERCADO_LABEL[submercado]} (${submercado})` },
    { campo: "Mês", texto: mesLabel(mesRef) },
    { campo: "PLD realizado médio", valor: overview?.pld_realizado_medio ?? null },
    { campo: "Semana atual (média)", valor: overview?.pld_semana_atual ?? null },
    {
      campo: overview?.pld_min ? `PLD mínimo (${fmtDateBR(overview.pld_min.data)})` : "PLD mínimo",
      valor: overview?.pld_min?.valor ?? null,
    },
    {
      campo: overview?.pld_max ? `PLD máximo (${fmtDateBR(overview.pld_max.data)})` : "PLD máximo",
      valor: overview?.pld_max?.valor ?? null,
    },
  ];
  if (temProjecao && projecao) {
    entradas.push(
      { campo: "Fechamento projetado", valor: projecao.fechamento_projetado },
      { campo: "Fechamento P10", valor: projecao.fechamento_p10 },
      { campo: "Fechamento P90", valor: projecao.fechamento_p90 },
      { campo: "Modelo", texto: projecao.modelo_versao },
    );
    if (prem) {
      entradas.push({
        campo: "Premissa DECOMP",
        texto: `${prem.revisao} · ${fmtPld(prem.valor)} · vigente ${fmtDateBR(prem.vigente_desde)}`,
      });
    }
  }
  const resumoRows: ResumoRow[] = entradas.map((e) => ({
    campo: e.campo,
    valorCell: e.texto !== undefined ? e.texto : e.valor != null ? num(e.valor) : null,
    valorStr: e.texto !== undefined ? e.texto : pdfNum(e.valor),
  }));
  const resumoColumns: ExportColumn<ResumoRow>[] = [
    { key: "campo", header: "Campo", format: (r) => String(r.campo) },
    { key: "valorCell", header: "Valor", format: (r) => r.valorCell as string | number | null },
  ];

  const extraSheets: ExtraSheet[] = [
    { name: "Semanas Operativas", columns: semanaColumns as ExportColumn[], rows: semanaRows },
    { name: "Resumo", columns: resumoColumns as ExportColumn[], rows: resumoRows },
  ];

  // ---- PDF ----
  const mesL = mesLabel(mesRef);
  const subL = `${SUBMERCADO_LABEL[submercado]} (${submercado})`;
  const pdfMainHead = ["Data", "Tipo", "PLD (R$/MWh)"];
  if (temProjecao) pdfMainHead.push("P10", "P90");
  const pdfMainBody = mainRows.map((r) => {
    const base = [fmtDateBR(r.data), r.tipo, pdfNum(r.pld)];
    if (temProjecao) base.push(pdfNum(r.p10), pdfNum(r.p90));
    return base;
  });

  const pdfInput: PdfInput = {
    filename,
    title: `Controle PLD — ${subL}`,
    subtitle: `Mês ${mesL}${prem ? ` · DECOMP ${prem.revisao} (vigente ${fmtDateBR(prem.vigente_desde)})` : ""}`,
    filtros: [
      `Submercado: ${subL}`,
      `Mês: ${mesL}`,
      temProjecao ? `Projeção ${projecao?.modelo_versao ?? "v1.5"}` : "Realizado (sem projeção)",
    ],
    tables: [
      {
        title: "Resumo",
        head: ["Campo", "Valor"],
        body: resumoRows.map((r) => [String(r.campo), String(r.valorStr)]),
      },
      { title: "PLD diário", head: pdfMainHead, body: pdfMainBody },
      {
        title: "Semanas Operativas",
        head: ["Revisão", "Período", "Status", "PLD médio", "Dias"],
        body: semanaRows.map((s) => [
          String(s.revisao), String(s.periodo), String(s.status),
          pdfNum(s.pld_medio as number | null), String(s.dias),
        ]),
      },
    ],
  };

  return { filename, temProjecao, mainColumns, mainRows, extraSheets, pdfInput };
}
